import React, { useState } from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { makeStyles } from '@material-ui/styles';
import {
  Avatar,
  Card,
  CardActions,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
  Tooltip,
  Hidden
} from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';
import RemoveRedEyeIcon from '@material-ui/icons/RemoveRedEye';
import { deepOrange, deepPurple, green } from '@material-ui/core/colors';
import { SortingColumn } from '../../../../components';
import { toCurrency, toDate } from '../../../../helpers/format';
import { getStatusName } from '../../../../helpers/status';

const useStyles = makeStyles(theme => ({
  root: { },
  content: {
    padding: 0
  },
  inner: {
    minWidth: 1050
  },
  actions: {
    justifyContent: 'flex-end'
  },
  view: {
      width: 80
  },
  edit: {
    width: 100
  },
  toOrder: {
    width: 180
  },
  A: {
    color: theme.palette.getContrastText(deepOrange[500]),
    backgroundColor: deepOrange[500],
  },
  C: {
    color: '#fff',
    backgroundColor: green[500],
  },
  P: {
    color: theme.palette.getContrastText(deepPurple[500]),
    backgroundColor: deepPurple[500],
  }
}));

const OfferTable = props => {
  const { className, offers, onViewOffer, onEditOffer, onOfferToOrder, page, setPage, ...rest } = props;

  const classes = useStyles();

  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [sort, setSort] = useState('reference');
  const [sortDirection, setSortDirection] = useState('desc');

  const handlePageChange = (event, page) => {
    setPage(page);
  };

  const handleRowsPerPageChange = event => {
    setPage(0);
    setRowsPerPage(event.target.value);
  };

  const onSorting = (newSort, newSortDirection) => {
    setSort(newSort);
    setSortDirection(newSortDirection);
  };

  const sortedOffers = () => {
    return offers.sort((a, b) => {
      const aValue = sort.split('.').reduce((c, d) => c[d], a);
      const bValue = sort.split('.').reduce((c, d) => c[d], b);
      if (aValue > bValue) {
        return sortDirection === 'asc' ? 1 : -1;
      }
      if (aValue < bValue) {
        return sortDirection === 'asc' ? -1 : 1;
      }
      return 0;
    });
  };

  return (
    <Card
      {...rest}
      className={clsx(classes.root, className)}
    >
      <CardContent className={classes.content}>
        <PerfectScrollbar>
          <div className={classes.inner}>
            <Table>
              <TableHead>
                <TableRow>
                  <SortingColumn sort={"reference"} label={"# Referencia"} currentSort={sort} currentSortDirection={sortDirection} onSorting={(newSort, newSortDirection) => onSorting(newSort, newSortDirection)} />
                  <SortingColumn sort={"date"} label={"Fecha"} currentSort={sort} currentSortDirection={sortDirection} onSorting={(newSort, newSortDirection) => onSorting(newSort, newSortDirection)} />
                  <SortingColumn sort={"code"} label={"Código"} currentSort={sort} currentSortDirection={sortDirection} onSorting={(newSort, newSortDirection) => onSorting(newSort, newSortDirection)} />
                  <SortingColumn sort={"description"} label={"Descripción"} currentSort={sort} currentSortDirection={sortDirection} onSorting={(newSort, newSortDirection) => onSorting(newSort, newSortDirection)} />
                  <SortingColumn sort={"price"} label={"Precio"} currentSort={sort} currentSortDirection={sortDirection} onSorting={(newSort, newSortDirection) => onSorting(newSort, newSortDirection)} />
                  <SortingColumn sort={"qty"} label={"Cantidad"} currentSort={sort} currentSortDirection={sortDirection} onSorting={(newSort, newSortDirection) => onSorting(newSort, newSortDirection)} />
                  <SortingColumn sort={"unit"} label={"UDM"} currentSort={sort} currentSortDirection={sortDirection} onSorting={(newSort, newSortDirection) => onSorting(newSort, newSortDirection)} />
                  <SortingColumn sort={"status"} label={"Estado"} currentSort={sort} currentSortDirection={sortDirection} onSorting={(newSort, newSortDirection) => onSorting(newSort, newSortDirection)} />
                  <TableCell className={classes.view}></TableCell>
                  <TableCell className={classes.edit}></TableCell>
                  <TableCell className={classes.toOrder}></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedOffers().slice(page * rowsPerPage, (page * rowsPerPage) + rowsPerPage).map(offer => (
                  <TableRow
                    className={classes.tableRow}
                    hover
                    key={offer.id}
                  >
                    <TableCell>
                        {offer.reference}
                    </TableCell>
                    <TableCell>{toDate(offer.date)}</TableCell>
                    <TableCell>
                        {offer.code}
                    </TableCell>
                    <TableCell>{offer.description}</TableCell>
                    <TableCell>
                      {toCurrency(offer.price)}
                    </TableCell>
                    <TableCell>{offer.qty}</TableCell>
                    <TableCell>{offer.unit}</TableCell>
                    <TableCell>
                      <Tooltip title={getStatusName(offer.status)} placement="top-start">
                        <Avatar className={classes[offer.status]}>{offer.status}</Avatar>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                    <Button color="primary" variant="contained" onClick={() => onViewOffer(offer.offer_id)}>
                      <Hidden smUp><RemoveRedEyeIcon /></Hidden>
                      <Hidden xsDown>Ver</Hidden>
                    </Button>
                    </TableCell>
                    <TableCell>
                    {offer.is_editable && <Button color="secondary" variant="contained" onClick={() => onEditOffer(offer.offer_id)}>
                      <Hidden smUp><EditIcon /></Hidden>
                      <Hidden xsDown>Editar</Hidden>
                      </Button>}
                    </TableCell>
                    <TableCell>
                    {offer.is_orderable && <Button color="default" variant="contained" onClick={() => onOfferToOrder(offer.offer_id)}>Hacer pedido</Button>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </PerfectScrollbar>
      </CardContent>
      <CardActions className={classes.actions}>
        <TablePagination
          component="div"
          count={offers.length}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handleRowsPerPageChange}
          page={page}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[10, 25, 50]}
        />
      </CardActions>
    </Card>
  );
};

OfferTable.propTypes = {
  className: PropTypes.string,
  offers: PropTypes.array.isRequired
};

export default OfferTable;
